/**
 * ================================================================================================
 *  הגדרות — THE LANDING, AND NOTHING ELSE ON IT
 * ================================================================================================
 *
 * Owner, 2026-10-28, with two reference screens beside a screenshot of ours:
 *
 *     *"לא צריך שיהיה בבר הגדרות ומתחת פרטי הארגון ומתחת משתמשים ואז בטאבים יהיה עוד פעם רשום את
 *     זה, זה כפילות מיותרת. מספיק שבבר יהיה רשום הגדרות ושלוחצים, אזי במסך יש את ההגדרות ...
 *     אצלנו במערכת יש ממש כפילות מיותרת והמון הסברים מיותרים."*
 *
 * THE DUPLICATION HE IS POINTING AT IS BETWEEN THE RAIL AND THE BAR, not between two bars. The rail
 * listed `הגדרות` and expanded it into `פרטי הארגון · משתמשים · תפקידים והרשאות · אבטחה וביקורת`;
 * the tab bar above the content then listed the same screens again; and this screen's own strip
 * listed them a third time. Three navigations of one area, stacked.
 *
 * ── WHAT THE REFERENCE DOES, AND WHAT IS COPIED FROM IT ─────────────────────────────────────────
 *
 * The reference's settings LANDING is a title and a GRID OF CATEGORY CARDS. No tab bar on it at
 * all. Each card is an icon, a name, and ONE short line saying what is inside. Clicking one opens
 * that category, and the category page carries a tab strip of ITS OWN topics.
 *
 * So: the rail has one entry, this screen is the grid, and the tabs live one level down where they
 * are topics rather than a second menu. `shell/areas.ts` declares this address area-less for that
 * reason — the bar is absent here by construction and not by styling.
 *
 * ── AND THE CARDS NAME ONLY WHAT EXISTS ─────────────────────────────────────────────────────────
 *
 * The Owner's own list of categories included a connection to an Open Finance service. There is no
 * such connection in this product, so there is no card for it: *"אין להציג רכיב כפעיל כשהוא אינו
 * מחובר"* is his rule, and a card that opens nothing is the same defect wearing an icon.
 *
 * ── THE LINE UNDER EACH NAME IS ONE LINE ───────────────────────────────────────────────────────
 *
 * *"המון הסברים מיותרים"* is the same instruction as *"המערכת צריכה להיות מקצועית עם כותרות
 * לפעולות מבלי לתת הסברים מיותרים"*. The reference carries one short line per card and so does
 * this: enough to choose a card, not an explanation of the product. `no-standing-prose.test.ts`
 * holds the length.
 */
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { Icon } from '../shell/Icon';
import type { IconName } from '../shell/Icon';
import { routes } from '../routes';

interface SettingsCategory {
  readonly title: TextKey;
  /** ONE line. The reference carries one; so does this. */
  readonly lead: TextKey;
  readonly href: string;
  readonly icon: IconName;
}

/**
 * THE CATEGORIES, IN THE ORDER A PERSON LOOKS FOR THEM: the organisation itself, then its people,
 * then what they may do, then the companies it manages, then what it is connected to, then what it
 * pays, then what protects it.
 *
 * Every one of these opens a screen that already exists and already carries its own capability.
 * This screen grants nothing and reads nothing: a card is a link, and the gate decides whether the
 * screen behind it answers.
 */
const CATEGORIES: readonly SettingsCategory[] = [
  {
    title: 'settings.cat.organisation',
    lead: 'settings.cat.organisationLead',
    href: routes.tenantOrganisation,
    icon: 'settings',
  },
  {
    title: 'settings.cat.users',
    lead: 'settings.cat.usersLead',
    href: routes.tenantUsers,
    icon: 'users',
  },
  {
    title: 'settings.cat.roles',
    lead: 'settings.cat.rolesLead',
    href: routes.tenantRoles,
    icon: 'user-check',
  },
  {
    title: 'settings.cat.companies',
    lead: 'settings.cat.companiesLead',
    href: routes.tenantCompanies,
    icon: 'building',
  },
  {
    title: 'settings.cat.connections',
    lead: 'settings.cat.connectionsLead',
    href: routes.tenantSources,
    icon: 'plug',
  },
  {
    title: 'settings.cat.subscription',
    lead: 'settings.cat.subscriptionLead',
    href: routes.tenantSubscription,
    icon: 'credit-card',
  },
  /*
   * §11/§12, BESIDE THE SUBSCRIPTION AND NOT INSIDE IT. The subscription card says what the
   * office HAS; this one is what it PAID and how it stops paying. That is the boundary
   * `TenantSubscriptionScreen`'s own header draws (08 §8.4), kept in the navigation as well as in
   * the code.
   */
  {
    title: 'billing.cardTitle',
    lead: 'billing.cardBody',
    href: routes.tenantBilling,
    icon: 'tag',
  },
  {
    /*
     * 0030/0031. AFTER THE CONNECTIONS AND BEFORE THE SUBSCRIPTION is not where this sits; it
     * sits before `security`, which keeps the order the screen's own header describes: the
     * organisation, its people, what they may do, its clients, what it is connected to, what it
     * reports and when, what it pays, what protects it. The card leads to a screen that already
     * carries `tasks.item.read`, so this grants nothing.
     */
    title: 'settings.cat.deadlines',
    lead: 'settings.cat.deadlinesLead',
    href: routes.tenantReminderRules,
    icon: 'calendar',
  },
  {
    title: 'settings.cat.security',
    lead: 'settings.cat.securityLead',
    href: routes.tenantSecurity,
    icon: 'shield',
  },
  /*
   * ── LAST, AND IT IS THE ONLY ONE THAT IS NOT ABOUT THE OFFICE. **Owner, 2026-11-01.** ───
   *
   * *"העבר את ההתאמה האישית מדף הבית אל ההגדרות."* Every category above changes something every
   * colleague sees -- the organisation, its people, what they may do, its clients, its
   * connections, what it pays, what it reports, what protects it. This one changes one person's
   * home screen, which is why it reads last rather than beside the organisation's own record.
   *
   * IT IS GATED ON NOTHING, and that is correct rather than an omission: the screen behind it
   * writes a user preference through `user.preference.update`, which every session holds, and the
   * choice *"אינה מעניקה הרשאות או מנוי"*. A capability on this card would be a capability on
   * arranging one's own cards.
   */
  {
    title: 'settings.cat.personalisation',
    lead: 'settings.cat.personalisationLead',
    href: routes.tenantPersonalisation,
    /*
     * `layers` AND NOT `settings`. Measured on the rendered landing: `settings` is already the
     * organisation card's icon, and two cards wearing one glyph in a grid of nine is a grid a
     * person scans by reading every title. The cards ARE layers -- this screen chooses which of
     * them are stacked on the home screen.
     */
    icon: 'layers',
  },
];

export function TenantAdminScreen(): JSX.Element {
  const ctx = useWorkContext();
  const { t } = useLocale();

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('settings.title')} requires="TENANT" actual={ctx.surface} />;
  }

  return (
    <AppShell
      screenId="tenant-settings"
      titleHe={t('settings.title')}
      /*
       * EVERY STRING ON THIS SCREEN GOES THROUGH `t()`. `translatedScreens.test.ts` fails the
       * build if one Hebrew literal survives here, which is what makes this a claim and not a note.
       */
      translated
      backToParent={{ labelHe: t('settings.backToHome'), href: routes.tenantHome }}
    >
      <div className="settings-grid">
        {CATEGORIES.map((category) => (
          /*
            THE WHOLE CARD IS THE LINK, and that is why it is an `<a>` rather than a box with a
            button inside it. A card you can only open by hitting a word in its corner is the
            arrangement the Owner was looking at; one target, the size of the card, is the fix.
          */
          <a key={category.title} className="settings-card" href={category.href}>
            <span className="settings-card__icon icon-chip" aria-hidden="true">
              <Icon name={category.icon} className="settings-card__glyph" />
            </span>
            <span className="settings-card__text">
              <span className="settings-card__title">{t(category.title)}</span>
              <span className="settings-card__lead">{t(category.lead)}</span>
            </span>
          </a>
        ))}
      </div>
    </AppShell>
  );
}
